import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, TrendingUp, AlertCircle, CheckCircle2, Info, Crown, Lock } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from 'axios';
import UpgradeModal from '@/components/UpgradeModal';
import PremiumBadge from '@/components/PremiumBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Trends = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7);
  const [trendsData, setTrendsData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    fetchTrendsData();
    fetchSubscriptionStatus();
  }, [timeRange]);

  const fetchSubscriptionStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/subscriptions/status`, {
        withCredentials: true
      });
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleTimeRangeChange = (days) => {
    // Gate 30+ days for premium users only
    if (days > 14 && !subscriptionStatus?.has_subscription) {
      setShowUpgradeModal(true);
      return;
    }
    setTimeRange(days);
  };

  const fetchTrendsData = async () => {
    setLoading(true);
    try {
      const { data } = await healthApi.getTrendsData(timeRange);
      setTrendsData(data);
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'attention':
        return <Info className="h-5 w-5 text-yellow-600" />;
      default:
        return <TrendingUp className="h-5 w-5 text-blue-600" />;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'positive':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'attention':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4" data-testid="trends-page">
      <div className="max-w-7xl mx-auto pt-4 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <TrendingUp className="h-8 w-8 mr-3 text-indigo-600" />
            Health Pattern Analysis
          </h1>
          <p className="text-gray-600 mt-2">Monitor your wellness patterns for early awareness and proactive care</p>
        </div>

        {/* Time Range Selector */}
        <div className="flex gap-3 mb-6">
          <Button
            variant={timeRange === 7 ? 'default' : 'outline'}
            onClick={() => setTimeRange(7)}
            data-testid="7-day-button"
          >
            7 Days
          </Button>
          <Button
            variant={timeRange === 30 ? 'default' : 'outline'}
            onClick={() => setTimeRange(30)}
            data-testid="30-day-button"
          >
            30 Days
          </Button>
        </div>

        {/* Trend Insights */}
        {trendsData?.insights && trendsData.insights.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">📊 Trend Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trendsData.insights.map((insight, index) => (
                <Card
                  key={index}
                  className={`border-2 ${getInsightColor(insight.type)}`}
                  data-testid={`insight-${index}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      {getInsightIcon(insight.type)}
                      <p className="text-sm font-medium">{insight.message}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No Data Message */}
        {trendsData?.data_points === 0 && (
          <Card className="mb-6">
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No check-in data available for this period</p>
              <Button onClick={() => navigate('/check-in')}>Complete Your First Check-in</Button>
            </CardContent>
          </Card>
        )}

        {/* Charts */}
        {trendsData?.data_points > 0 && (
          <div className="space-y-6">
            {/* Wellness Score Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Wellness Score Over Time</CardTitle>
                <CardDescription>Track your overall wellness progress</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendsData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="short_date" stroke="#6b7280" />
                    <YAxis domain={[0, 100]} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="wellness_score" 
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6', r: 4 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Sleep Quality Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Sleep Quality</CardTitle>
                <CardDescription>Your sleep quality ratings (1-10)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendsData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="short_date" stroke="#6b7280" />
                    <YAxis domain={[0, 10]} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="sleep_quality" 
                      stroke="#a855f7" 
                      strokeWidth={3}
                      dot={{ fill: '#a855f7', r: 4 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Stress Level Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Stress Levels</CardTitle>
                <CardDescription>Daily stress patterns (1=Low, 2=Medium, 3=High)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={trendsData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="short_date" stroke="#6b7280" />
                    <YAxis domain={[0, 3]} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value) => {
                        const labels = { 1: 'Low', 2: 'Medium', 3: 'High' };
                        return labels[value] || value;
                      }}
                    />
                    <Bar 
                      dataKey="stress_level" 
                      fill="#f59e0b"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Energy Level Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Energy Levels</CardTitle>
                <CardDescription>Daily energy ratings (1-10)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendsData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="short_date" stroke="#6b7280" />
                    <YAxis domain={[0, 10]} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="energy_level" 
                      stroke="#f59e0b" 
                      strokeWidth={3}
                      dot={{ fill: '#f59e0b', r: 4 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Exercise Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Exercise Activity</CardTitle>
                <CardDescription>Minutes of exercise per day</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={trendsData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="short_date" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Bar 
                      dataKey="exercise" 
                      fill="#10b981"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Water Intake Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Water Intake</CardTitle>
                <CardDescription>Daily water consumption (Liters)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendsData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="short_date" stroke="#6b7280" />
                    <YAxis domain={[0, 4]} stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="water" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Trends;
